import { useEffect, useState } from "react";
import { X, UserPlus, Trash2 } from "lucide-react";
import { client, apiError, LABEL_COLORS } from "../lib/api";
import { Avatar } from "./Avatar";
import { useConfirm } from "./ConfirmDialog";
import { currentYear, teamYear, yearChoices } from "../lib/years";

export function MembersModal({ team, mode, members, myRole, currentUser, onClose, onChanged, onTeamUpdated, onTeamDeleted }) {
  const [tab, setTab] = useState(mode || "access");
  const [available, setAvailable] = useState([]);
  const [teamName, setTeamName] = useState(team.name);
  const [teamColor, setTeamColor] = useState(team.color);
  const [laporanDeadline, setLaporanDeadline] = useState(team.laporan_deadline || "");
  const [kkeDeadline, setKkeDeadline] = useState(team.kke_deadline || "");
  const [laporanLink, setLaporanLink] = useState(team.laporan_link || "");
  const [kkeLink, setKkeLink] = useState(team.kke_link || "");
  const [kertasLink, setKertasLink] = useState(team.kertas_link || "");
  const [teamYearValue, setTeamYearValue] = useState(teamYear(team));
  const [wilayah, setWilayah] = useState(team.wilayah || "");
  const [wilayahs, setWilayahs] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const isAdmin = myRole === "admin";
  const confirm = useConfirm();

  const loadAvailable = () => client.get(`/teams/${team.id}/available-members`).then(r => setAvailable(r.data));
  useEffect(() => { if (tab === "add") loadAvailable(); }, [tab, team.id]);
  useEffect(() => { client.get("/wilayahs").then(r => setWilayahs(r.data || [])).catch(() => setWilayahs([])); }, []);

  const add = async id => { await client.post(`/teams/${team.id}/members`, { user_id: id }); onChanged(); loadAvailable(); };
  const setRole = async (id, role) => { await client.patch(`/teams/${team.id}/members/${id}`, { role }); onChanged(); };
  const remove = async id => {
    const ok = await confirm({ title: "Keluarkan anggota ini?", body: "Mereka kehilangan akses ke tim ini.", confirmLabel: "Keluarkan", danger: true });
    if (ok) { await client.delete(`/teams/${team.id}/members/${id}`); onChanged(); }
  };

  const saveTeam = async () => {
    if (!teamName.trim()) return;
    setError("");
    try {
      await client.patch(`/teams/${team.id}`, {
        name: teamName.trim(), color: teamColor, year: Number(teamYearValue), wilayah: wilayah.trim() || "",
        laporan_deadline: laporanDeadline || null, kke_deadline: kkeDeadline || null,
        laporan_link: laporanLink.trim() || null, kke_link: kkeLink.trim() || null,
        kertas_link: kertasLink.trim() || null,
      });
      onTeamUpdated();
    }
    catch (e) { setError(apiError(e)); }
  };
  const deleteTeam = async () => {
    setError("");
    try { await client.delete(`/teams/${team.id}`); onTeamDeleted(); }
    catch (e) { setError(apiError(e)); setConfirmDelete(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section className="modal" onClick={e => e.stopPropagation()} data-testid="members-modal">
        <div className="modal-head"><h2>Anggota Tim</h2><button className="icon-button" onClick={onClose} data-testid="close-members-modal"><X size={18} /></button></div>
        <div className="view-toggle">
          <button className={tab === "access" ? "selected" : ""} onClick={() => setTab("access")} data-testid="members-tab-access">Anggota</button>
          <button className={tab === "add" ? "selected" : ""} onClick={() => setTab("add")} data-testid="members-tab-add">Tambah Anggota</button>
          {isAdmin && <button className={tab === "settings" ? "selected" : ""} onClick={() => setTab("settings")} data-testid="members-tab-settings">Pengaturan</button>}
        </div>
        {tab === "access" ? (
          <div className="members-list" data-testid="team-members-list">
            {members.map(m => (
              <div className="member-row" key={m.id} data-testid={`team-member-${m.id}`}>
                <Avatar id={m.id} name={m.name} photo={m.avatar} />
                <div><b>{m.name}</b><small>{m.email}</small></div>
                {myRole === "admin" ? (
                  <>
                    <select value={m.team_role} onChange={e => setRole(m.id, e.target.value)} data-testid={`member-role-${m.id}`}><option value="member">Member</option><option value="admin">Admin</option></select>
                    {m.id !== currentUser.id && <button className="danger-link" onClick={() => remove(m.id)} data-testid={`remove-member-${m.id}`}>Keluarkan</button>}
                  </>
                ) : <span className="muted">{m.team_role === "admin" ? "Admin" : "Member"}</span>}
              </div>
            ))}
          </div>
        ) : tab === "add" ? (
          <div className="members-list" data-testid="available-members-list">
            {available.length ? available.map(u => (
              <div className="member-row" key={u.id} data-testid={`available-member-${u.id}`}>
                <Avatar id={u.id} name={u.name} photo={u.avatar} />
                <div><b>{u.name}</b><small>{u.email}</small></div>
                <button className="secondary" onClick={() => add(u.id)} data-testid={`add-member-${u.id}`}><UserPlus size={13} /> Tambah</button>
              </div>
            )) : <p className="muted" style={{ padding: "18px" }}>Semua pengguna sudah menjadi anggota.</p>}
          </div>
        ) : (
          <div className="team-settings" data-testid="team-settings-panel">
            <label className="sf-label">NAMA TIM<input value={teamName} onChange={e => setTeamName(e.target.value)} data-testid="team-name-input" /></label>
            <label className="sf-label">TAHUN PENUGASAN
              <select value={teamYearValue} onChange={e => setTeamYearValue(Number(e.target.value))} data-testid="team-year-select">
                {yearChoices(teamYearValue).map(y => <option key={y} value={y}>{y}{y === currentYear() ? " (tahun ini)" : y < currentYear() ? " (arsip)" : ""}</option>)}
              </select>
            </label>
            <p className="muted" style={{ marginTop: -8, fontSize: 12 }}>Pindahkan ke tahun lalu untuk mengarsipkan tim di sidebar tanpa menghapus datanya.</p>
            <label className="sf-label">WILAYAH / PEMDA
              <input value={wilayah} onChange={e => setWilayah(e.target.value)} list="team-wilayahs" placeholder="Contoh: Kabupaten Karawang" data-testid="team-wilayah-input" />
              <datalist id="team-wilayahs">{wilayahs.map(w => <option key={w} value={w} />)}</datalist>
            </label>
            <label className="sf-label">WARNA<div className="td-label-swatches">{LABEL_COLORS.map(c => <button type="button" key={c} style={{ background: c, outline: teamColor === c ? "2px solid #10213b" : "none" }} onClick={() => setTeamColor(c)} data-testid={`team-edit-color-${c}`} />)}</div></label>
            <p className="sf-section">Pengumpulan hasil</p>
            <p className="muted sf-hint">Folder Drive tempat anggota mengunggah deliverable. Bukan dokumen yang dikerjakan di papan.</p>
            <label className="sf-label">TENGGAT LAPORAN<input type="date" value={laporanDeadline} onChange={e => setLaporanDeadline(e.target.value)} data-testid="team-laporan-deadline-input" /></label>
            <label className="sf-label">FOLDER UNGGAH LAPORAN<input value={laporanLink} onChange={e => setLaporanLink(e.target.value)} placeholder="https://drive.google.com/…" data-testid="team-laporan-link-input" /></label>
            <label className="sf-label">TENGGAT KKE<input type="date" value={kkeDeadline} onChange={e => setKkeDeadline(e.target.value)} data-testid="team-kke-deadline-input" /></label>
            <label className="sf-label">FOLDER UNGGAH KKE<input value={kkeLink} onChange={e => setKkeLink(e.target.value)} placeholder="https://drive.google.com/…" data-testid="team-kke-link-input" /></label>
            <p className="sf-section">Dokumen kerja</p>
            <p className="muted sf-hint">Spreadsheet atau folder kertas kerja yang dibuka dari papan tugas. Kalau filenya lebih dari satu, taruh semua di satu folder Drive lalu tempel link foldernya.</p>
            <label className="sf-label">LINK KERTAS KERJA<input value={kertasLink} onChange={e => setKertasLink(e.target.value)} placeholder="https://docs.google.com/… atau folder Drive" data-testid="team-kertas-link-input" /></label>
            <button className="primary" onClick={saveTeam} disabled={!teamName.trim()} data-testid="save-team-button">Simpan Perubahan</button>

            <div className="team-danger-zone">
              <p className="sf-label">ZONA BERBAHAYA</p>
              {!confirmDelete ? (
                <button className="td-sidebar-btn danger" onClick={() => setConfirmDelete(true)} data-testid="delete-team-button"><Trash2 size={14} /> Hapus Tim</button>
              ) : (
                <div className="team-delete-confirm" data-testid="delete-team-confirm">
                  <p className="small">Yakin hapus tim "{team.name}"? Semua tugas, list, pengumuman, dan dokumen di tim ini akan terhapus permanen dan tidak bisa dikembalikan.</p>
                  <div>
                    <button className="secondary" onClick={() => setConfirmDelete(false)} data-testid="cancel-delete-team-button">Batal</button>
                    <button className="td-sidebar-btn danger" onClick={deleteTeam} data-testid="confirm-delete-team-button">Ya, Hapus Permanen</button>
                  </div>
                </div>
              )}
            </div>
            {error && <div className="error" data-testid="team-settings-error">{error}</div>}
          </div>
        )}
      </section>
    </div>
  );
}
